import { ReactNode, RefObject } from 'react';
import { BoxProps } from '@mui/material';
import {
  ConnectorType,
  DateRangeInput,
  Filter,
  Metric,
  MetricsByConnectorType,
  MetricValuesRowsGroupedByConnector,
  Split,
} from 'src/utils/gql/types';

import { UpdateConversationContextPayload } from '../Dashboard/types';

export enum OperatorEnum {
  Equal = 'EQUAL',
  NotEqual = 'NOT_EQUAL',
  Contain = 'CONTAIN',
  NotContain = 'NOT_CONTAIN',
  GreaterThan = 'GREATER_THAN',
  LowerThan = 'LOWER_THAN',
  In = 'IN',
  NotIn = 'NOT_IN',
  Range = 'RANGE',
}

interface Connectors {
  shopifyConnectorId?: string;
  magentoConnectorId?: string;
  magentoConnectorEnabled?: boolean;
  facebookConnectorId?: string;
  googleAdsConnectorId?: string;
  googleAnalytics4ConnectorId?: string;
  googleSearchConsoleConnectorId?: string;
}

export interface FiltersProps {
  toogleFilterMetrics: () => void;
  isShownMetricFilters: boolean;
  googleAdsMetricId: string;
  shopifyFilterValues: { key: string; values: string[] }[];
  magentoFilterValues: { key: string; values: string[] }[];
  connectors: Connectors;
}

export interface DashboardTableInitData {
  rows: MetricValuesRowsGroupedByConnector;
  dateRange: DateRangeInput;
  columnOrder?: string[]; // ordine personalizzato delle colonne (splits e metrics)
  sortConfig?: {
    columnId: string | null;
    direction: 'asc' | 'desc';
  }; // ordinamento righe (non persistito di default)
}

export interface DashboardTableProps {
  onCloseTable: (values: UpdateConversationContextPayload) => void;
  title: string;
  initData?: DashboardTableInitData;
  metricsIsShown: boolean;
  rows: TableRowsType[][];
  columnOrder: string[];
  changeColumnOrder: (val: string[]) => void;
  onConfigChange?: (config: any) => void;
  onTitleChange?: (newTitle: string) => void;
  titleInputRef?: RefObject<HTMLInputElement>;
  onTitleSave?: (newTitle: string) => void;
  showTitleSaved?: boolean;
  hideSaveAndDeleteControls?: boolean; // Se true, nasconde i pulsanti Save e Delete
  // Props opzionali per i connettori (se non fornite, usa il hook interno)
  googleAdsConnectorId?: string;
  shopifyConnectorId?: string;
  facebookConnectorId?: string;
  googleAnalytics4ConnectorId?: string;
  clientAccountId?: string;
  propertyId?: string;
  compareDateRange?: DateRangeInput | null; // Periodo di comparazione per mostrare variazioni
}

export interface DashboardHeaderProps {
  isSmallScreen: boolean;
  title: string;
  googleAdsMetrics: Metric[];
  shopifyFilterValues: { key: string; values: string[] }[];
  magentoFilterValues: { key: string; values: string[] }[];
  connectors: Connectors;
  onBackButtonClick: () => void;
  onTitleChange?: (newTitle: string) => void;
  titleInputRef?: RefObject<HTMLInputElement>;
  onTitleSave?: (newTitle: string) => void;
  showTitleSaved?: boolean;
}

export interface ParametrsMobileContentProps {
  onBackClick: () => void;
  onSave: () => void;
  googleAdsMetrics: Metric[];
  shopifyFilterValues: { key: string; values: string[] }[];
  magentoFilterValues: { key: string; values: string[] }[];
  connectors: Connectors;
}

export interface FilterProps {
  filter: FilterWithValue;
  formulaItemIndex: number;
}

export interface MetricsMobileContentProps {
  onBackClick: () => void;
  onSave: () => void;
  connectors: Connectors;
}

export interface ParametrsModalProps {
  closeModal: () => void;
  open: boolean;
  children: ReactNode;
}

export enum MetricFilterOperatorLabel {
  Is = 'Is',
  IsNot = 'Is Not',
  Contains = 'Contains',
  NotContains = 'Does Not Contain',
  GreaterThan = 'Greater than',
  LowerThan = 'Lower than',
}

export enum MetricFilterOperatorKey {
  Is = 'IN',
  IsNot = 'NOT_IN',
  Contains = 'CONTAIN',
  NotContains = 'NOT_CONTAIN',
  GreaterThan = 'GREATER_THAN',
  LowerThan = 'LOWER_THAN',
}

export interface FilterCollapseProps {
  filter: FilterWithValue;
  formulaItemIndex: number;
}

export type DashboardForm = {
  startDate: string;
  endDate: string;
  splits: Split[];
  filters: FilterWithValue[];
  metrics: Metric[];
};

export interface AppliedFilter {
  operator: string;
  values: string[];
}

export type AppliedFilterGroup =
  | AppliedFilter
  | { filtersGroup: AppliedFilter[] };

export interface FilterWithValue extends FrontendFilter {
  values: Array<AppliedFilterGroup>;
}

export type TableRowsType = {
  id: string;
  columnId: string;
  title: string;
  type: ConnectorType | MetricsByConnectorType;
  icon?: JSX.Element;
  value: string | number;
  isSplit: boolean;
};

interface ConnectorData {
  metrics: Metric[];
  loading: boolean;
  hasConnector: boolean;
  hasChoosenConnector?: boolean;
}

export interface MetricsProps {
  facebookData: ConnectorData;
  googleAdsData: ConnectorData;
  googleAnalyticsData: ConnectorData;
  googleSearchConsoleData?: ConnectorData;
  shopifyData: ConnectorData;
  magentoData?: ConnectorData;
  kipifyData?: ConnectorData;
  customFormulaData: ConnectorData;
  expandedCollapseType: (ConnectorType | MetricsByConnectorType)[];
  onToggleCollapse: (
    type: ConnectorType | MetricsByConnectorType,
    isSearch?: boolean,
  ) => void;
  debFunction?: () => void;
  openOrCloseAllMetrics: (isOpen: boolean) => void;
}

export interface SplitFilterProps {
  splits: Split[];
  debFunction?: () => void;
}

export enum InputName {
  startDate = 'startDate',
  endDate = 'endDate',
  splits = 'splits',
  metrics = 'metrics',
  filters = 'filters',
}

export interface CalendarProps {
  name: InputName.startDate | InputName.endDate;
  disabled?: boolean;
}

export interface BadgeProps extends BoxProps {
  badgeContent: number;
}

export interface MetricsDropdownProps {
  type: ConnectorType | MetricsByConnectorType;
  label: string;
  icon: ReactNode;
  expanded: boolean;
  metricsByCategory: Record<string, Metric[]>;
  loading?: boolean;
  onClickMetric?: (metric: Metric) => void;
  onToggle: (type: ConnectorType | MetricsByConnectorType) => void;
  debFunction?: () => void;
  showConfigureButton?: boolean;
}

export interface FiltersBlockProps extends MetricsInputProps {
  facebookMetrics: Metric[];
  shopifyMetrics: Metric[];
  googleAdsMetrics: Metric[];
  googleAnalyticsMetrics: Metric[];
  shopifyFilterValues: { key: string; values: string[] }[];
  magentoFilterValues: { key: string; values: string[] }[];
  connectors: Connectors;
  debFunction?: () => void;
}

export interface FiltersBlockProps {
  metricsIsShown: boolean;
  closeMetrics: () => void;
  showMetrics: () => void;
}

export type MetricsInputProps = {
  metricsIsShown: boolean;
  closeMetrics: () => void;
  showMetrics: () => void;
};

export enum FrontendFilterType {
  SimpleFilter = 'SimpleFilter',
  MetricFilter = 'MetricFilter',
}

export interface FrontendFilter
  extends Pick<
    Filter,
    | 'id'
    | 'label'
    | 'operators'
    | 'key'
    | 'shouldAvailableValuesBeRequested'
    | 'availableValues'
    | 'expectedReturnValueType'
  > {
  type: FrontendFilterType;
  connectorType: ConnectorType | MetricsByConnectorType;
}
